"use client";

import { Info, Mail, MessageSquare, Zap } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../shadcn/ui/tooltip";

import { ChannelPreference } from "./types";
import { useEffect } from "react";

interface ChannelPreferenceSelectorProps {
  value: ChannelPreference;
  onChange: (value: ChannelPreference) => void;
  totalFans?: number;
  emailCount?: number;
  phoneCount?: number;
  estimatedCost?: number;
  creditBalance?: number;
  onBuyCredits?: () => void;
}

const options: {
  value: ChannelPreference;
  label: string;
  subtitle: string;
  icons: ("mail" | "sms")[];
}[] = [
  {
    value: "email_only",
    label: "Email",
    subtitle: "Email fans directly",
    icons: ["mail"],
  },
  {
    value: "sms_only",
    label: "SMS",
    subtitle: "Text fans directly",
    icons: ["sms"],
  },
  {
    value: "email_and_sms",
    label: "Email & SMS",
    subtitle: "Reach fans on both",
    icons: ["mail", "sms"],
  },
];

function getDistribution(
  preference: ChannelPreference,
  total: number,
  emailCount: number,
  phoneCount: number
) {
  if (total === 0) return { email: 0, sms: 0 };
  switch (preference) {
    case "email_only":
      return { email: emailCount, sms: 0 };
    case "sms_only":
      return { email: 0, sms: phoneCount };
    case "email_and_sms":
      return { email: emailCount, sms: phoneCount };
    default:
      return { email: emailCount, sms: 0 };
  }
}

function getDisabledOptions(
  emailCount: number,
  phoneCount: number
): Record<ChannelPreference, string | null> {
  return {
    email_only:
      emailCount === 0
        ? "No fans with email are in the selected audience"
        : null,
    sms_only:
      phoneCount === 0 ? "No fans with SMS are in the selected audience" : null,
    email_and_sms:
      emailCount === 0 || phoneCount === 0
        ? emailCount === 0
          ? "No fans with email are in the selected audience"
          : "No fans with SMS are in the selected audience"
        : null,
  };
}

function ChannelIcon({
  type,
  className,
}: {
  type: "mail" | "sms";
  className?: string;
}) {
  if (type === "mail") return <Mail className={className ?? "h-5 w-5"} />;
  return <MessageSquare className={className ?? "h-5 w-5"} />;
}

export function ChannelPreferenceSelector({
  value,
  onChange,
  totalFans = 0,
  emailCount: emailCountProp,
  phoneCount: phoneCountProp,
  estimatedCost,
  creditBalance,
  onBuyCredits,
}: ChannelPreferenceSelectorProps) {
  const resolvedEmailCount = emailCountProp ?? totalFans;
  const resolvedPhoneCount = phoneCountProp ?? 0;
  const disabledOptions = getDisabledOptions(
    resolvedEmailCount,
    resolvedPhoneCount
  );

  // Auto-fallback: if the current selection becomes disabled, pick the first enabled option
  useEffect(() => {
    if (disabledOptions[value]) {
      const fallback = options.find((opt) => !disabledOptions[opt.value]);
      if (fallback) onChange(fallback.value);
    }
  }, [value, resolvedEmailCount, resolvedPhoneCount, onChange]);

  const distribution = getDistribution(
    value,
    totalFans,
    resolvedEmailCount,
    resolvedPhoneCount
  );
  const showEmail = distribution.email > 0;
  const showSms = distribution.sms > 0;

  const hasInsufficientCredits =
    estimatedCost !== undefined &&
    creditBalance !== undefined &&
    estimatedCost > creditBalance;

  const balanceColor =
    estimatedCost === undefined || creditBalance === undefined
      ? "text-[#787B7E]"
      : hasInsufficientCredits
        ? "text-[#F1B81F]"
        : "text-[#38A93C]";

  return (
    <div className="space-y-3">
      {/* Channel Cards */}
      <div className="flex flex-col sm:flex-row gap-3">
        {options.map((option) => {
          const disabledReason = disabledOptions[option.value];
          const isDisabled = !!disabledReason;
          const isSelected = value === option.value;

          const card = (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                if (!isDisabled) onChange(option.value);
              }}
              disabled={isDisabled}
              className={`flex flex-1 flex-row items-center gap-3 rounded-lg border p-4 transition-colors ${
                isSelected
                  ? "border-[#FE5E25] bg-[#FFF5EC]"
                  : "border-[#C7C8C9] bg-white"
              } ${isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
            >
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                  isSelected ? "bg-[rgba(254,94,37,0.15)]" : "bg-[#F2F2F3]"
                }`}
              >
                {option.value === "email_and_sms" ? (
                  <Zap
                    className={`h-5 w-5 ${isSelected ? "text-[#FE5E25]" : "text-[#787B7E]"}`}
                  />
                ) : (
                  <ChannelIcon
                    type={option.icons[0]}
                    className={`h-5 w-5 ${isSelected ? "text-[#FE5E25]" : "text-[#787B7E]"}`}
                  />
                )}
              </div>
              <div className="flex flex-col items-start text-left">
                <span
                  className={`text-sm font-semibold ${isSelected ? "text-[#FE5E25]" : "text-[#29333C]"}`}
                >
                  {option.label}
                </span>
                <span
                  className={`text-xs ${isSelected ? "text-[#FE5E25]" : "text-[#787B7E]"}`}
                >
                  {option.subtitle}
                </span>
              </div>
            </button>
          );

          if (isDisabled) {
            return (
              <TooltipProvider key={option.value} delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex flex-1">{card}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{disabledReason}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }

          return card;
        })}
      </div>

      {/* Reach Summary Card */}
      {(estimatedCost !== undefined || creditBalance !== undefined) && (
        <div className="rounded-[10px] border border-[#C7C8C9] bg-white">
          {/* Cost section (top) */}
          <div className="px-5 pt-5 pb-4">
            <p className={`text-sm font-semibold text-[#29333C]`}>
              Cost:{" "}
              {estimatedCost !== undefined
                ? `${estimatedCost.toLocaleString()} credits`
                : "--"}
            </p>
            <p className={`mt-0.5 text-xs ${balanceColor}`}>
              Balance:{" "}
              {creditBalance !== undefined
                ? `${creditBalance.toLocaleString()} credits`
                : "--"}
              {onBuyCredits && (
                <>
                  {" \u00b7 "}
                  <span className="text-[#787B7E]">Need More?</span>{" "}
                  <button
                    type="button"
                    onClick={onBuyCredits}
                    className="text-[#FE5E25] underline font-medium"
                  >
                    Click Here
                  </button>
                </>
              )}
            </p>
          </div>

          {/* Reach section (bottom) */}
          {totalFans > 0 && (showEmail || showSms) && (
            <div className="px-5 pb-5">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-[#29333C]">
                  Fans you&apos;ll reach
                </span>
                <Info className="h-3.5 w-3.5 text-[#787B7E]" />
              </div>
              <p className="mt-0.5 text-sm text-[#29333C]">
                {showEmail &&
                  !showSms &&
                  `${distribution.email.toLocaleString()} fans will receive your message via Email.`}
                {showSms &&
                  !showEmail &&
                  `${distribution.sms.toLocaleString()} fans will receive your message via SMS.`}
                {showEmail &&
                  showSms &&
                  `${distribution.email.toLocaleString()} fans via Email and ${distribution.sms.toLocaleString()} fans via SMS.`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
